// =============================================================================
// Reports — unified hub faithfully covering the legacy POS + IMS report catalog
// (Sales summary/department/category/type/cashier/house, products, tax, X-Out /
// Z-Out, refunds, payouts, inventory valuation, low stock, job costing, gift
// certificates). Config-driven: each report declares its params + columns and
// renders as a faithful printable table with totals + CSV export.
// =============================================================================
import { useEffect, useMemo, useState, useCallback } from 'react'
import { Download, Printer } from 'lucide-react'
import * as R from '../services/reports'
import { downloadCsv } from '../services/reports'
import { PageHeader, Card, Button, Spinner } from '../components/shared/ui'
import { currency, number } from '../lib/format'

type Params = 'range' | 'day' | 'none'
type Fmt = 'text' | 'money' | 'int' | 'num'
type Row = Record<string, unknown>

interface ColSpec { key: string; header: string; fmt?: Fmt }
interface ReportDef {
  key: string
  label: string
  category: string
  params: Params
  columns: ColSpec[]
  load: (p: { start: string; end: string; day: string }) => Promise<Row[]>
}

function todayStr() { return new Date().toISOString().slice(0, 10) }
function daysAgo(n: number) { const d = new Date(); d.setDate(d.getDate() - n); return d.toISOString().slice(0, 10) }
function endExclusive(end: string) { const d = new Date(end); d.setDate(d.getDate() + 1); return d.toISOString().slice(0, 10) }
const asRows = <T,>(x: T[]) => x as unknown as Row[]

// ---- Catalog ----------------------------------------------------------------
const CATALOG: ReportDef[] = [
  // Sales
  { key: 'sales_summary', label: 'Sales Summary', category: 'Sales', params: 'range',
    columns: [{ key: 'day', header: 'Date' }, { key: 'invoice_count', header: 'Invoices', fmt: 'int' }, { key: 'total_sales', header: 'Sales', fmt: 'money' }, { key: 'total_tax', header: 'Tax', fmt: 'money' }, { key: 'avg_ticket', header: 'Avg Ticket', fmt: 'money' }, { key: 'total_qty', header: 'Qty', fmt: 'num' }, { key: 'taxable_sales', header: 'Taxable', fmt: 'money' }, { key: 'non_taxable_sales', header: 'Non-Taxable', fmt: 'money' }],
    load: p => R.salesSummary(p.start, endExclusive(p.end)).then(asRows) },
  { key: 'sale_department', label: 'Sale by Department', category: 'Sales', params: 'range',
    columns: [{ key: 'department', header: 'Department' }, { key: 'units', header: 'Units', fmt: 'num' }, { key: 'revenue', header: 'Revenue', fmt: 'money' }, { key: 'cost', header: 'Cost', fmt: 'money' }, { key: 'profit', header: 'Profit', fmt: 'money' }],
    load: p => R.saleByDepartment(p.start, endExclusive(p.end)).then(asRows) },
  { key: 'sale_category', label: 'Sale by Category', category: 'Sales', params: 'range',
    columns: [{ key: 'category', header: 'Category' }, { key: 'units', header: 'Units', fmt: 'num' }, { key: 'revenue', header: 'Revenue', fmt: 'money' }, { key: 'cost', header: 'Cost', fmt: 'money' }, { key: 'profit', header: 'Profit', fmt: 'money' }],
    load: p => R.saleByCategory(p.start, endExclusive(p.end)).then(asRows) },
  { key: 'sale_type', label: 'Sale by Type', category: 'Sales', params: 'range',
    columns: [{ key: 'inventory_type', header: 'Type' }, { key: 'units', header: 'Units', fmt: 'num' }, { key: 'revenue', header: 'Revenue', fmt: 'money' }, { key: 'cost', header: 'Cost', fmt: 'money' }, { key: 'profit', header: 'Profit', fmt: 'money' }, { key: 'avg_price', header: 'Avg Price', fmt: 'money' }],
    load: p => R.typeSales(p.start, endExclusive(p.end)).then(asRows) },
  { key: 'sale_cashier', label: 'Sale by Cashier', category: 'Sales', params: 'range',
    columns: [{ key: 'clerk_id', header: 'Cashier' }, { key: 'invoice_count', header: 'Invoices', fmt: 'int' }, { key: 'total_sales', header: 'Sales', fmt: 'money' }, { key: 'total_tax', header: 'Tax', fmt: 'money' }],
    load: p => R.saleByCashier(p.start, endExclusive(p.end)).then(asRows) },
  { key: 'house_wise', label: 'House-wise Sales', category: 'Sales', params: 'range',
    columns: [{ key: 'customer', header: 'Customer' }, { key: 'invoice_count', header: 'Invoices', fmt: 'int' }, { key: 'total_sales', header: 'Sales', fmt: 'money' }],
    load: p => R.houseWiseSale(p.start, endExclusive(p.end)).then(asRows) },
  { key: 'hourly', label: 'Hourly Sales', category: 'Sales', params: 'day',
    columns: [{ key: 'hour', header: 'Hour' }, { key: 'invoice_count', header: 'Invoices', fmt: 'int' }, { key: 'total_sales', header: 'Sales', fmt: 'money' }, { key: 'total_tax', header: 'Tax', fmt: 'money' }, { key: 'avg_ticket', header: 'Avg Ticket', fmt: 'money' }, { key: 'total_qty', header: 'Qty', fmt: 'num' }],
    load: p => R.hourlySales(p.day).then(asRows) },
  { key: 'monthly', label: 'Monthly Trend (12mo)', category: 'Sales', params: 'none',
    columns: [{ key: 'month', header: 'Month' }, { key: 'invoice_count', header: 'Invoices', fmt: 'int' }, { key: 'total_sales', header: 'Sales', fmt: 'money' }],
    load: () => R.monthlyTrend(12).then(asRows) },
  { key: 'outsale', label: 'Out-Sale by Date', category: 'Sales', params: 'range',
    columns: [{ key: 'day', header: 'Date' }, { key: 'units', header: 'Units', fmt: 'num' }, { key: 'revenue', header: 'Revenue', fmt: 'money' }, { key: 'invoice_count', header: 'Invoices', fmt: 'int' }, { key: 'cost', header: 'Cost', fmt: 'money' }, { key: 'profit', header: 'Profit', fmt: 'money' }],
    load: p => R.outSaleDatewise(p.start, endExclusive(p.end)).then(asRows) },
  // Products
  { key: 'top_products', label: 'Top Products', category: 'Products', params: 'range',
    columns: [{ key: 'product_name', header: 'Product' }, { key: 'units', header: 'Units', fmt: 'num' }, { key: 'revenue', header: 'Revenue', fmt: 'money' }, { key: 'cost', header: 'Cost', fmt: 'money' }, { key: 'profit', header: 'Profit', fmt: 'money' }, { key: 'avg_price', header: 'Avg Price', fmt: 'money' }],
    load: p => R.productSales(p.start, endExclusive(p.end), 100).then(asRows) },
  { key: 'product_summary', label: 'Product Sale Summary', category: 'Products', params: 'range',
    columns: [{ key: 'product_name', header: 'Product' }, { key: 'units', header: 'Units', fmt: 'num' }, { key: 'revenue', header: 'Revenue', fmt: 'money' }, { key: 'cost', header: 'Cost', fmt: 'money' }, { key: 'profit', header: 'Profit', fmt: 'money' }],
    load: p => R.productSaleSummary(p.start, endExclusive(p.end)).then(asRows) },
  // Tax & Tender
  { key: 'sales_tax', label: 'Sales Tax', category: 'Tax & Tender', params: 'range',
    columns: [{ key: 'day', header: 'Date' }, { key: 'taxable', header: 'Taxable', fmt: 'money' }, { key: 'non_taxable', header: 'Non-Taxable', fmt: 'money' }, { key: 'tax', header: 'Tax', fmt: 'money' }, { key: 'total', header: 'Total', fmt: 'money' }],
    load: p => R.salesTaxReport(p.start, endExclusive(p.end)).then(asRows) },
  { key: 'xout', label: 'Payment Breakdown (range)', category: 'Tax & Tender', params: 'range',
    columns: [{ key: 'payment_method', header: 'Payment Method' }, { key: 'payment_count', header: 'Count', fmt: 'int' }, { key: 'total_amount', header: 'Amount', fmt: 'money' }, { key: 'avg_amount', header: 'Avg', fmt: 'money' }],
    load: p => R.xoutReport(p.start, endExclusive(p.end)).then(asRows) },
  { key: 'day_totals', label: 'Daily Z-Out Totals', category: 'Tax & Tender', params: 'day',
    columns: [{ key: 'gross_sales', header: 'Gross Sales', fmt: 'money' }, { key: 'total_tax', header: 'Tax', fmt: 'money' }, { key: 'taxable_sales', header: 'Taxable', fmt: 'money' }, { key: 'non_taxable_sales', header: 'Non-Taxable', fmt: 'money' }, { key: 'invoice_count', header: 'Invoices', fmt: 'int' }, { key: 'avg_ticket', header: 'Avg Ticket', fmt: 'money' }, { key: 'total_qty', header: 'Qty', fmt: 'num' }, { key: 'payout_total', header: 'Payouts', fmt: 'money' }, { key: 'refund_total', header: 'Refunds', fmt: 'money' }, { key: 'net_sales', header: 'Net', fmt: 'money' }],
    load: p => R.dayTotals(p.day).then(r => (r ? [r] : []) as unknown as Row[]) },
  // Refunds & Payouts
  { key: 'refunds', label: 'Refund Summary', category: 'Refunds & Payouts', params: 'range',
    columns: [{ key: 'day', header: 'Date' }, { key: 'refund_count', header: 'Refunds', fmt: 'int' }, { key: 'total_qty', header: 'Qty', fmt: 'num' }, { key: 'total_refunded', header: 'Refunded', fmt: 'money' }, { key: 'total_tax', header: 'Tax', fmt: 'money' }, { key: 'avg_refund', header: 'Avg Refund', fmt: 'money' }],
    load: p => R.refundSummary(p.start, endExclusive(p.end)).then(asRows) },
  { key: 'payouts', label: 'Payout Summary', category: 'Refunds & Payouts', params: 'range',
    columns: [{ key: 'day', header: 'Date' }, { key: 'payout_count', header: 'Payouts', fmt: 'int' }, { key: 'total_amount', header: 'Amount', fmt: 'money' }, { key: 'avg_amount', header: 'Avg', fmt: 'money' }],
    load: p => R.payoutSummary(p.start, endExclusive(p.end)).then(asRows) },
  // Inventory (IMS)
  { key: 'inv_valuation', label: 'Inventory Valuation', category: 'Inventory', params: 'none',
    columns: [{ key: 'inventory_type', header: 'Type' }, { key: 'item_count', header: 'Items', fmt: 'int' }, { key: 'on_hand', header: 'On Hand', fmt: 'num' }, { key: 'cost_value', header: 'Cost Value', fmt: 'money' }, { key: 'retail_value', header: 'Retail Value', fmt: 'money' }, { key: 'avg_cost', header: 'Avg Cost', fmt: 'money' }, { key: 'avg_retail', header: 'Avg Retail', fmt: 'money' }, { key: 'markup_pct', header: 'Markup %', fmt: 'num' }],
    load: () => R.inventoryValuation().then(asRows) },
  { key: 'low_stock', label: 'Low Stock', category: 'Inventory', params: 'none',
    columns: [{ key: 'product_name', header: 'Product' }, { key: 'inventory_type', header: 'Type' }, { key: 'on_hand', header: 'On Hand', fmt: 'num' }, { key: 'reorder_level', header: 'Reorder', fmt: 'int' }, { key: 'suggested_qty', header: 'Order Qty', fmt: 'int' }, { key: 'retail_price', header: 'Retail', fmt: 'money' }, { key: 'vendor', header: 'Vendor' }],
    load: () => R.lowStock(5).then(asRows) },
  // Job Costing (IMS)
  { key: 'job_costing', label: 'Job Costing by Month', category: 'Job Costing', params: 'range',
    columns: [{ key: 'month', header: 'Month' }, { key: 'job_count', header: 'Jobs', fmt: 'int' }, { key: 'revenue', header: 'Revenue', fmt: 'money' }, { key: 'cost', header: 'Cost', fmt: 'money' }, { key: 'profit', header: 'Profit', fmt: 'money' }],
    load: p => R.jobCosting(p.start, endExclusive(p.end)).then(asRows) },
  { key: 'job_profit_type', label: 'Job Profit by Type', category: 'Job Costing', params: 'range',
    columns: [{ key: 'item_type', header: 'Item Type' }, { key: 'units', header: 'Units', fmt: 'num' }, { key: 'revenue', header: 'Revenue', fmt: 'money' }, { key: 'cost', header: 'Cost', fmt: 'money' }, { key: 'profit', header: 'Profit', fmt: 'money' }],
    load: p => R.jobProfitByType(p.start, endExclusive(p.end)).then(asRows) },
  // Profit & Loss
  { key: 'profit_loss', label: 'Master P&L', category: 'Profit & Loss', params: 'range',
    columns: [{ key: 'line', header: 'Line' }, { key: 'revenue', header: 'Revenue', fmt: 'money' }, { key: 'cost', header: 'Cost', fmt: 'money' }, { key: 'profit', header: 'Net', fmt: 'money' }],
    load: p => R.profitLoss(p.start, endExclusive(p.end)).then(asRows) },
  { key: 'profit_designer', label: 'Profitability by Designer', category: 'Profit & Loss', params: 'range',
    columns: [{ key: 'designer', header: 'Designer' }, { key: 'job_count', header: 'Jobs', fmt: 'int' }, { key: 'revenue', header: 'Revenue', fmt: 'money' }, { key: 'cost', header: 'Cost', fmt: 'money' }, { key: 'profit', header: 'Profit', fmt: 'money' }],
    load: p => R.profitabilityByDesigner(p.start, endExclusive(p.end)).then(asRows) },
  { key: 'spring_cleanup', label: 'Spring Cleanup Jobs', category: 'Job Costing', params: 'range',
    columns: [{ key: 'bill_code', header: 'Job #' }, { key: 'customer', header: 'Customer' }, { key: 'bill_date', header: 'Date' }, { key: 'invoice_total', header: 'Total', fmt: 'money' }],
    load: p => R.springCleanup(p.start, endExclusive(p.end)).then(asRows) },
  { key: 'job_totals_designer', label: 'Job Totals by Designer', category: 'Job Costing', params: 'range',
    columns: [{ key: 'designer', header: 'Designer' }, { key: 'job_count', header: 'Jobs', fmt: 'int' }, { key: 'invoice_count', header: 'Invoiced', fmt: 'int' }, { key: 'total', header: 'Total', fmt: 'money' }, { key: 'avg_job', header: 'Avg Job', fmt: 'money' }],
    load: p => R.jobTotalsByDesigner(p.start, endExclusive(p.end)).then(asRows) },
  { key: 'job_totals_foreman', label: 'Job Totals by Foreman', category: 'Job Costing', params: 'range',
    columns: [{ key: 'foreman', header: 'Foreman' }, { key: 'job_count', header: 'Jobs', fmt: 'int' }, { key: 'invoice_count', header: 'Invoiced', fmt: 'int' }, { key: 'total', header: 'Total', fmt: 'money' }, { key: 'avg_job', header: 'Avg Job', fmt: 'money' }],
    load: p => R.jobTotalsByForeman(p.start, endExclusive(p.end)).then(asRows) },
  { key: 'est_inv_category', label: 'Estimates & Invoices by Category', category: 'Job Costing', params: 'range',
    columns: [{ key: 'category', header: 'Category' }, { key: 'estimate_count', header: 'Est #', fmt: 'int' }, { key: 'estimate_total', header: 'Estimates', fmt: 'money' }, { key: 'invoice_count', header: 'Inv #', fmt: 'int' }, { key: 'invoice_total', header: 'Invoices', fmt: 'money' }, { key: 'variance', header: 'Variance', fmt: 'money' }],
    load: p => R.estInvByCategory(p.start, endExclusive(p.end)).then(asRows) },
  // Appointments (IMS)
  { key: 'appt_month', label: 'Appointments by Month', category: 'Appointments', params: 'range',
    columns: [{ key: 'month', header: 'Month' }, { key: 'appt_count', header: 'Appointments', fmt: 'int' }, { key: 'go_ahead_count', header: 'Go-Ahead', fmt: 'int' }, { key: 'no_go_count', header: 'No-Go', fmt: 'int' }, { key: 'conversion_pct', header: 'Conv %', fmt: 'num' }],
    load: p => R.appointmentsByMonth(p.start, endExclusive(p.end)).then(asRows) },
  { key: 'appt_zip', label: 'Appointments by Zip', category: 'Appointments', params: 'range',
    columns: [{ key: 'zip', header: 'Zip' }, { key: 'city', header: 'City' }, { key: 'appt_count', header: 'Appointments', fmt: 'int' }, { key: 'go_ahead_count', header: 'Go-Ahead', fmt: 'int' }, { key: 'conversion_pct', header: 'Conv %', fmt: 'num' }],
    load: p => R.appointmentsByZip(p.start, endExclusive(p.end)).then(asRows) },
  { key: 'designer_schedule', label: 'Designer Schedule', category: 'Appointments', params: 'range',
    columns: [{ key: 'designer', header: 'Designer' }, { key: 'appt_date', header: 'Date' }, { key: 'appt_time', header: 'Time' }, { key: 'customer', header: 'Customer' }, { key: 'city', header: 'City' }],
    load: p => R.designerSchedule(p.start, endExclusive(p.end)).then(asRows) },
  // Gift Certificates
  { key: 'gift_certs', label: 'Gift Certificate Summary', category: 'Gift Certificates', params: 'none',
    columns: [{ key: 'status', header: 'Status' }, { key: 'cert_count', header: 'Certificates', fmt: 'int' }, { key: 'issued_amount', header: 'Issued', fmt: 'money' }, { key: 'outstanding_balance', header: 'Outstanding', fmt: 'money' }, { key: 'redeemed_amount', header: 'Redeemed', fmt: 'money' }, { key: 'used_count', header: 'Used', fmt: 'int' }],
    load: () => R.giftCertificateSummary().then(asRows) },
]

const CATEGORIES = [...new Set(CATALOG.map(r => r.category))]

function fmtCell(v: unknown, fmt?: Fmt): string {
  if (v === null || v === undefined || v === '') return fmt === 'money' ? currency(0) : (fmt ? '0' : '—')
  if (fmt === 'money') return currency(Number(v))
  if (fmt === 'int') return number(Number(v))
  if (fmt === 'num') return number(Number(v))
  return String(v)
}

export default function Reports() {
  const [activeKey, setActiveKey] = useState(CATALOG[0].key)
  const [start, setStart] = useState(daysAgo(30))
  const [end, setEnd] = useState(todayStr())
  const [day, setDay] = useState(todayStr())
  const [rows, setRows] = useState<Row[]>([])
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const def = useMemo(() => CATALOG.find(r => r.key === activeKey)!, [activeKey])

  const load = useCallback(async () => {
    setLoading(true); setError(null)
    try {
      setRows(await def.load({ start, end, day }))
    } catch (e) {
      console.error(e)
      setError(e instanceof Error ? e.message : 'Failed to load report')
      setRows([])
    } finally {
      setLoading(false)
    }
  }, [def, start, end, day])

  useEffect(() => { load() }, [load])

  const totals = useMemo(() => {
    const t: Record<string, number> = {}
    for (const c of def.columns) {
      if (c.fmt === 'money' || c.fmt === 'num' || c.fmt === 'int') {
        t[c.key] = rows.reduce((s, r) => s + Number(r[c.key] ?? 0), 0)
      }
    }
    return t
  }, [rows, def])

  const exportCsv = () => downloadCsv(`${def.key}-report.csv`, rows)

  return (
    <div>
      <PageHeader
        title="Reports"
        subtitle="Faithful POS + IMS report catalog"
        actions={
          <>
            <Button variant="secondary" onClick={exportCsv} className="print:hidden"><Download size={16} /> Export CSV</Button>
            <Button variant="secondary" onClick={() => window.print()} className="print:hidden"><Printer size={16} /> Print</Button>
          </>
        }
      />

      {/* Print-only header: visible only on the printed page */}
      <div className="hidden print:block mb-6">
        <h1 className="text-2xl font-bold text-gray-900">{def.label}</h1>
        <p className="text-sm text-gray-600 mt-1">
          {def.params === 'range' && `Date range: ${start} to ${end}`}
          {def.params === 'day' && `Date: ${day}`}
          {def.params === 'none' && 'Live snapshot'}
          <span className="mx-2">·</span>
          Generated {new Date().toLocaleString()}
        </p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
        {/* Report list grouped by category */}
        <Card className="p-3 lg:col-span-1 self-start print:hidden">
          {CATEGORIES.map(cat => (
            <div key={cat} className="mb-3">
              <p className="px-2 text-xs font-semibold uppercase tracking-wide text-gray-400 mb-1">{cat}</p>
              {CATALOG.filter(r => r.category === cat).map(r => (
                <button key={r.key} onClick={() => setActiveKey(r.key)}
                  className={`block w-full text-left px-3 py-1.5 rounded-lg text-sm ${activeKey === r.key ? 'bg-green-700 text-white' : 'text-gray-700 hover:bg-gray-50'}`}>
                  {r.label}
                </button>
              ))}
            </div>
          ))}
        </Card>

        <div className="lg:col-span-3 space-y-4">
          <Card className="p-4 print:hidden">
            <div className="flex gap-4 flex-wrap items-end">
              {def.params === 'range' && (
                <>
                  <div>
                    <label className="block text-xs font-medium text-gray-500 mb-1">Start Date</label>
                    <input type="date" value={start} onChange={e => setStart(e.target.value)} className="px-3 py-2 rounded-lg border border-gray-300 text-sm" />
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-gray-500 mb-1">End Date</label>
                    <input type="date" value={end} onChange={e => setEnd(e.target.value)} className="px-3 py-2 rounded-lg border border-gray-300 text-sm" />
                  </div>
                </>
              )}
              {def.params === 'day' && (
                <div>
                  <label className="block text-xs font-medium text-gray-500 mb-1">Date</label>
                  <input type="date" value={day} onChange={e => setDay(e.target.value)} className="px-3 py-2 rounded-lg border border-gray-300 text-sm" />
                </div>
              )}
              {def.params === 'none' && <p className="text-sm text-gray-500">Live snapshot — no date range.</p>}
            </div>
          </Card>

          <Card className="p-0 overflow-hidden print:shadow-none print:border-gray-300">
            <div className="px-5 py-3 border-b border-gray-200 flex items-center justify-between print:px-0 print:py-2">
              <h3 className="font-semibold text-gray-800 print:hidden">{def.label}</h3>
              <span className="text-xs text-gray-400 print:hidden">{rows.length} row{rows.length === 1 ? '' : 's'}</span>
            </div>
            {loading ? <Spinner label="Generating report…" /> : error ? (
              <p className="text-center text-red-600 py-10 text-sm">{error}</p>
            ) : rows.length === 0 ? (
              <p className="text-center text-gray-400 py-10">No data for this report / period.</p>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="text-gray-600 border-b border-gray-200 bg-gray-50">
                      {def.columns.map(c => (
                        <th key={c.key} className={`px-3 py-2 font-semibold ${c.fmt && c.fmt !== 'text' ? 'text-right' : 'text-left'}`}>{c.header}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {rows.map((r, i) => (
                      <tr key={i} className="border-b border-gray-100 hover:bg-green-50/40">
                        {def.columns.map(c => (
                          <td key={c.key} className={`px-3 py-1.5 ${c.fmt && c.fmt !== 'text' ? 'text-right tabular-nums' : 'text-left'}`}>{fmtCell(r[c.key], c.fmt)}</td>
                        ))}
                      </tr>
                    ))}
                  </tbody>
                  {Object.keys(totals).length > 0 && rows.length > 1 && (
                    <tfoot>
                      <tr className="border-t-2 border-gray-300 font-semibold bg-gray-50">
                        {def.columns.map((c, idx) => (
                          <td key={c.key} className={`px-3 py-2 ${c.fmt && c.fmt !== 'text' ? 'text-right tabular-nums' : 'text-left'}`}>
                            {idx === 0 ? 'Total' : (c.key in totals ? fmtCell(totals[c.key], c.fmt) : '')}
                          </td>
                        ))}
                      </tr>
                    </tfoot>
                  )}
                </table>
              </div>
            )}
          </Card>
        </div>
      </div>
    </div>
  )
}
